/**
 * useAudioRecorder hook - Browser audio recording using MediaRecorder API.
 *
 * Records audio from the user's microphone and returns it as a Blob
 * for server-side transcription. This approach keeps API credentials
 * secure on the backend rather than exposing them in the browser.
 *
 * Supported formats: webm (Chrome/Edge), mp4 (Safari), ogg (Firefox)
 */

import { useState, useCallback, useRef, useEffect } from 'react'

/**
 * MediaRecorderErrorEvent interface
 * TypeScript's DOM types don't include this, so we define it ourselves
 * based on the W3C MediaRecorder API specification
 */
interface MediaRecorderErrorEvent extends Event {
  error: DOMException
}

/**
 * Audio recorder state
 */
export interface AudioRecorderState {
  isRecording: boolean
  isPaused: boolean
  error: string | null
  isSupported: boolean
  /** Duration of current recording in seconds */
  duration: number
}

/**
 * Audio recorder actions
 */
export interface AudioRecorderActions {
  /** Start recording audio */
  startRecording: () => Promise<void>
  /** Stop recording and return the audio blob */
  stopRecording: () => Promise<Blob | null>
  /** Pause recording */
  pauseRecording: () => void
  /** Resume recording */
  resumeRecording: () => void
  /** Cancel recording without returning audio */
  cancelRecording: () => void
}

/**
 * Hook return type
 */
export interface UseAudioRecorderReturn extends AudioRecorderState, AudioRecorderActions {}

/**
 * Check if MediaRecorder is supported
 */
function isMediaRecorderSupported(): boolean {
  return typeof window !== 'undefined' && 'MediaRecorder' in window
}

/**
 * Get the best supported audio MIME type
 */
function getSupportedMimeType(): string {
  // OGG/Opus preferred: Azure Fast Transcription handles OPUS/OGG natively,
  // whereas WebM relies on GStreamer which intermittently rejects browser-
  // recorded WebM containers with InvalidAudioFormat (422).
  // Chrome/Edge/Firefox all support audio/ogg;codecs=opus.
  // Safari doesn't support OGG but falls through to audio/mp4.
  const types = [
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/mp4',
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/wav',
  ]

  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type
    }
  }

  // Fallback - let the browser decide
  return ''
}

/**
 * useAudioRecorder hook
 *
 * Provides audio recording functionality using the browser's MediaRecorder API.
 * Returns audio as a Blob for server-side transcription.
 */
export function useAudioRecorder(): UseAudioRecorderReturn {
  const [isRecording, setIsRecording] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [duration, setDuration] = useState(0)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const recordingStartTimeRef = useRef<number | null>(null)

  const isSupported = isMediaRecorderSupported()

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current)
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop())
      }
    }
  }, [])

  /**
   * Start recording audio
   */
  const startRecording = useCallback(async () => {
    if (!isSupported) {
      setError('Audio recording is not supported in this browser')
      return
    }

    setError(null)
    audioChunksRef.current = []

    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000, // Good for speech recognition
        },
      })

      mediaStreamRef.current = stream

      // Create MediaRecorder with best supported format
      const mimeType = getSupportedMimeType()
      const options: MediaRecorderOptions = mimeType ? { mimeType } : {}
      const recorder = new MediaRecorder(stream, options)

      mediaRecorderRef.current = recorder

      // Collect audio chunks
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }

      // Handle errors with specific messages
      recorder.onerror = (event) => {
        // Extract error details from MediaRecorderErrorEvent
        const errorEvent = event as MediaRecorderErrorEvent
        const errorName = errorEvent.error?.name || 'UnknownError'
        const errorMessage = errorEvent.error?.message || 'Unknown recording error'

        console.error('[useAudioRecorder] Recording error:', { errorName, errorMessage, event })

        // Provide user-friendly error messages
        let userMessage: string
        switch (errorName) {
          case 'InvalidStateError':
            userMessage = 'Recording was interrupted. Please try again.'
            break
          case 'NotSupportedError':
            userMessage = 'Audio format not supported. Please try a different browser.'
            break
          case 'SecurityError':
            userMessage = 'Microphone access was blocked. Please check your browser permissions.'
            break
          default:
            userMessage = `Recording error: ${errorMessage}`
        }

        setError(userMessage)
        setIsRecording(false)
        setIsPaused(false)
      }

      // Start recording with short timeslice for faster data capture
      // 100ms ensures quick taps still capture audio chunks
      recorder.start(100)
      setIsRecording(true)
      setIsPaused(false)

      // Track duration
      recordingStartTimeRef.current = Date.now()
      setDuration(0)
      durationIntervalRef.current = setInterval(() => {
        if (recordingStartTimeRef.current) {
          setDuration(Math.floor((Date.now() - recordingStartTimeRef.current) / 1000))
        }
      }, 1000)

    } catch (err) {
      console.error('[useAudioRecorder] Failed to start recording:', err)

      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError') {
          setError('Microphone access denied. Please allow microphone access to use voice input.')
        } else if (err.name === 'NotFoundError') {
          setError('No microphone found. Please connect a microphone.')
        } else {
          setError(`Microphone error: ${err.message}`)
        }
      } else {
        setError('Failed to start recording')
      }
    }
  }, [isSupported])

  /**
   * Stop recording and return the audio blob
   */
  const stopRecording = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current

      if (!recorder || recorder.state === 'inactive') {
        // Log why we're returning null - helps debug race conditions
        console.warn('[useAudioRecorder] stopRecording called but no active recording', {
          hasRecorder: !!recorder,
          state: recorder?.state,
        })
        resolve(null)
        return
      }

      // Clear duration tracking
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current)
        durationIntervalRef.current = null
      }
      recordingStartTimeRef.current = null

      recorder.onstop = () => {
        // Diagnostic logging for debugging short recordings
        console.info('[useAudioRecorder] Recording stopped', {
          chunks: audioChunksRef.current.length,
          totalSize: audioChunksRef.current.reduce((sum, c) => sum + c.size, 0),
        })

        try {
          // Stop all tracks
          if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => {
              try {
                track.stop()
              } catch (trackErr) {
                console.warn('[useAudioRecorder] Failed to stop track:', trackErr)
              }
            })
            mediaStreamRef.current = null
          }

          // Create blob from chunks
          const mimeType = recorder.mimeType || 'audio/webm'
          const audioBlob = new Blob(audioChunksRef.current, { type: mimeType })
          audioChunksRef.current = []

          setIsRecording(false)
          setIsPaused(false)
          setDuration(0)

          resolve(audioBlob)
        } catch (err) {
          console.error('[useAudioRecorder] Error in onstop handler:', err)
          setError('Failed to save recording. Please try again.')
          setIsRecording(false)
          setIsPaused(false)
          setDuration(0)
          audioChunksRef.current = []
          resolve(null)
        }
      }

      // Request any remaining buffered data before stopping
      // This ensures short recordings still capture audio
      if (recorder.state === 'recording') {
        recorder.requestData()
      }
      recorder.stop()
      mediaRecorderRef.current = null
    })
  }, [])

  /**
   * Pause recording
   */
  const pauseRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current

    if (!recorder) {
      console.warn('[useAudioRecorder] pauseRecording called but no recorder exists')
      return
    }

    if (recorder.state !== 'recording') {
      console.warn('[useAudioRecorder] Cannot pause - recorder state is:', recorder.state)
      return
    }

    try {
      recorder.pause()
      setIsPaused(true)

      // Pause duration tracking
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current)
        durationIntervalRef.current = null
      }
    } catch (err) {
      console.error('[useAudioRecorder] Failed to pause recording:', err)
      setError('Failed to pause recording. Please stop and try again.')
    }
  }, [])

  /**
   * Resume recording
   */
  const resumeRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current

    if (!recorder) {
      console.warn('[useAudioRecorder] resumeRecording called but no recorder exists')
      return
    }

    if (recorder.state !== 'paused') {
      console.warn('[useAudioRecorder] Cannot resume - recorder state is:', recorder.state)
      return
    }

    try {
      recorder.resume()
      setIsPaused(false)

      // Resume duration tracking (continue from where we left off)
      durationIntervalRef.current = setInterval(() => {
        setDuration((prev) => prev + 1)
      }, 1000)
    } catch (err) {
      console.error('[useAudioRecorder] Failed to resume recording:', err)
      setError('Failed to resume recording. Please stop and try again.')
    }
  }, [])

  /**
   * Cancel recording without returning audio
   */
  const cancelRecording = useCallback(() => {
    // Clear duration tracking
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current)
      durationIntervalRef.current = null
    }
    recordingStartTimeRef.current = null

    const recorder = mediaRecorderRef.current

    // Stop recorder with error handling
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop()
      } catch (err) {
        console.warn('[useAudioRecorder] Error stopping recorder during cancel:', err)
      }
    }

    // Always clean up tracks (separate try-catch to ensure execution)
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => {
        try {
          track.stop()
        } catch (err) {
          console.warn('[useAudioRecorder] Error stopping track during cancel:', err)
        }
      })
      mediaStreamRef.current = null
    }

    audioChunksRef.current = []
    mediaRecorderRef.current = null

    setIsRecording(false)
    setIsPaused(false)
    setDuration(0)
  }, [])

  return {
    isRecording,
    isPaused,
    error,
    isSupported,
    duration,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
  }
}

export default useAudioRecorder
